import React, { Suspense, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import { Link } from "react-router-dom";
import moment from "moment";
import queryString from "query-string";
import LoadingSpinner from "../../components/UI/loadingSpinner/LoadingSpinner";
import { ROUTES } from "../../constants/routes";
import { downloadCSV } from "../../utils/csv";
import {
  PAGINATION_SEQUENCE,
  shopFileHeaders,
  shopHeaderMapping,
} from "../../constants";
import ExportIcon from "../../components/icons/ExportIcon";
import { IShop } from "../../interfaces/shop.interface";
import { getApi } from "../../services/http.service";
import Dropdown from "../../components/UI/dropdown/Dropdown";
import { STATES_OPTIONS } from "../../constants/states";
import DateRangeDropdown from "../../components/UI/date-range-dropdown/DateRangeDropdown";

function Shops() {
  const states = STATES_OPTIONS;
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState(searchParams.get("state") || "");
  const navigate = useNavigate();
  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [perPage, setPerPage] = useState(
    Number(searchParams.get("limit") || 10)
  );
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  // Data & Search
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState<IShop[]>([]);

  const [startDate, setStartDate] = useState(searchParams.get("start")||moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(searchParams.get("end") || moment().format("YYYY-MM-DD"));
  const [sortField, setSortField] = useState<string>(
    searchParams.get("sort") || "addedAt"
  );
  const [sortAsc, setSortAsc] = useState<boolean>(
    searchParams.get("order") === "asc"
  );

  useEffect(() => {
    fetchNews(page, perPage, search);
  }, [page, perPage, search, selected, startDate, endDate]);

  const fetchNews = async (page: number, limit: number, search: string) => {
    try {
      setLoading(true);
      const result = await getApi(ROUTES.SHOP_LIST, {
        page,
        limit,
        search,
        state: selected,
        startDate,
        endDate,
      });
      if (result.code === 200) {
        setFilteredData(result?.data);
        setTotalRows(result.total); // Set total records
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Page Change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle Rows per Page Change
  const handlePerRowsChange = (newPerPage: number, newPage: number) => {
    setPerPage(newPerPage);
    setPage(newPage);
  };

  const deleteRow = (item: IShop) => {
    console.log(item);
  };

  const columns: TableColumn<IShop>[] = useMemo(
    () => [
      {
        id:"shopName",
        name: "Shop Name",
        cell: (row: any) => (
          <Link
            className="nav-link text-hover-underline"
            to={`/shops/${row?._id}`}
          >
            {row?.shopName}
          </Link>
        ),
        selector: (row) => row.shopName,
        sortable: true,
        sortField:"shopName",
        maxWidth: "550px",
      },
      {
        name: "Shop Owner",
        selector: (row) => row.shopOwner,
        maxWidth: "170px",
      },
      {
        id:"shopContactNumber",
        name: "Shop Contact",
        selector: (row) => row.shopContactNumber || "-",
        sortable: true,
        sortField:"shopContactNumber",
        compact: true,
        width: "100px",
      },
      {
        id:"state",
        name: "State",
        selector: (row) => row.shopState,
        sortable: true,
        maxWidth: "140px",
        sortField:"state"
      },
      {
        name: "City",
        selector: (row) => row.shopCity,
        maxWidth: "140px",
      },
      {
        name: "Address",
        selector: (row) => row.shopAddress,
        maxWidth: "140px",
      },
      {
        name: "Area",
        selector: (row) => row.shopArea,
        maxWidth: "140px",
      },
      {
        id:"shopCategory",
        name: "Category",
        selector: (row) => row.shopCategory,
        maxWidth: "140px",
        sortable: true,
        sortField:"shopCategory"
      },
      {
        name: "Action",
        cell: (row) => (
          <span>
            <i className="bi bi-eye cursor-pointer" onClick={() => {}}></i>
            <i
              className="bi bi-pencil-square cursor-pointer mx-2"
              onClick={() => {}}
            ></i>
            <i
              className="bi bi-trash cursor-pointer"
              onClick={() => deleteRow(row)}
            ></i>
          </span>
        ),
        width: "120px",
      },
    ],
    []
  );
  function selectState(e: React.ChangeEvent<HTMLSelectElement>) {
    setSelected(() => e.target.value);
    const queryParams = queryString.stringify({
      sort: sortField,
      order: sortAsc ? "asc" : "desc",
      page,
      limit: perPage,
      state: e.target.value,
      start: startDate,
      end: endDate,
    });
    navigate(`?${queryParams}`, { replace: true });
  }

  function selectedChangeHandler(e: any) {
    const s = e[0].toString();
    const en = e[1]?.toString();

    if (s && en) {
      setStartDate(s);
      setEndDate(en);
      const queryParams = queryString.stringify({
        sort: sortField,
        order: sortAsc ? "asc" : "desc",
        page,
        limit: perPage,
        state: selected,
        start: s,
        end: en,
      });
      navigate(`?${queryParams}`, { replace: true });
    }
  }

  const handleSort = (column: TableColumn<IShop>, direction: any) => {
    const key = column.sortField?.toString() || "";
    setSortField(key);
    setSortAsc(direction === "asc");
    const queryParams = queryString.stringify({
      sort: key,
      order: direction,
      page,
      limit: perPage,
      state: selected,
      start: startDate,
      end: endDate,
    });
    navigate(`?${queryParams}`, { replace: true });
  };
  return (
    <section>
      <div className="d-flex justify-content-between flex-wrap">
        <h2 className="title">Shops</h2>
        <div className="d-flex">
          <DateRangeDropdown onChange={selectedChangeHandler} />
          <Dropdown
            dropdownData={states}
            bindLabel="state"
            bindValue="state"
            label="State"
            selected={selected}
            onChange={selectState}
          />
          <div>
            <button
              className="btn btn-primary"
              onClick={() => {
                downloadCSV(
                  filteredData,
                  shopFileHeaders,
                  shopHeaderMapping,
                  "Groove_Shops"
                );
              }}
            >
              <ExportIcon />
              <span className="ps-1"> Export</span>
            </button>

            <button
              className="btn btn-primary ms-2"
              onClick={() => navigate("/shops/add")}
            >
              <Icon icon={"ic:round-plus"} />
              <span className="ps-1"> Add Shop</span>
            </button>
            <button
              className="btn btn-primary ms-2"
              onClick={() => navigate("/shops/map")}
            >
              <Icon icon={"material-symbols:share-location"} />
              <span className="ps-1">View On Map</span>
            </button>
          </div>
        </div>
      </div>
      <Suspense>
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={PAGINATION_SEQUENCE}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          highlightOnHover
          striped
          progressPending={loading}
          progressComponent={<LoadingSpinner />}
          onSort={handleSort}
          sortServer={false}
          defaultSortFieldId={sortField}
          defaultSortAsc={sortAsc}
        />
      </Suspense>
    </section>
  );
}

export default Shops;
